/**
 * Block 13 PR-G Phase 4A — Factory для blank `ContractDraft`.
 *
 * Создаёт стартовое состояние формы 2.13.2 с разумными defaults:
 *   - id: `genUuid()` если не передан
 *   - templateKey: из `region` (UserRegion uppercase) + default lang per region
 *   - contractDate: сегодняшняя ISO date YYYY-MM-DD
 *   - contractCity: `template.meta.defaultCity` после load (или пустой если template
 *     ещё не загружен — Container перепишет после lazy template load)
 *   - durationDays: 30 (PRD §10.13.2)
 *   - warrantyTerm: 24, warrantyUnit: 'months' (PRD §10.13.2)
 *   - costSource: 'manual' (PRD §10.13.2 решение от 03.05.2026)
 *   - customerRequisites + executorRequisites: 7 default rows per template region
 *     (Aibek approval 2026-05-23 — populate on creation, не lazy on expand;
 *     упрощает rendering — нет conditional empty-state в UI)
 *
 * Reference:
 *   - ADR-0025 §Q2 (ContractDraft state shape, all fields default-able)
 *   - PRD §10.13.2 «Стоимость и сроки», «Реквизиты сторон»
 */

import {
  CONTRACT_CURRENCY_PER_REGION,
  CONTRACT_DEFAULT_LANGUAGE_PER_REGION,
  type ContractCurrencyCode,
  type ContractDraft,
  type ContractRegion,
  type ContractRequisite,
  type ContractSpecRow,
  type ContractTemplateKey,
  buildContractTemplateKey,
} from '@pomoshnik/shared';
import type { UserRegion } from '@pomoshnik/shared';

import { DEFAULT_COMPOSITION_ROWS } from '@/features/calculator/quote/compositionRows';
import { genUuid } from '@/lib/uuid';

import { buildDefaultRequisites } from './contractRequisitesDefaults';
import type { SpecBlockSeedCustomRow, SpecBlockSeedValues } from './contractReducer';

/**
 * Map `UserRegion` (uppercase auth store domain) → `ContractRegion` (lowercase
 * template domain). Single source of truth для conversion — выводит template key.
 */
export function userRegionToContractRegion(region: UserRegion): ContractRegion {
  switch (region) {
    case 'KZ':
      return 'kz';
    case 'RU':
      return 'ru';
    case 'UZ':
      return 'uz';
  }
}

/**
 * Phase 6.5F Amendment A7 (2026-05-25) — single block_cost row per block.
 *
 * **Before (6.5B A2):** 4 fixed rows (facade_material + corpus_material +
 * hardware + block_cost).
 *
 * **After (6.5F A7):** ОДНА fixed row `block_cost` («Стоимость», cost: 0).
 * 3 material slots DROPPED — пользователь добавляет позиции (Столешница,
 * Фасады, Фурнитура, Корзина выдвижная, etc.) вручную через «+ Добавить
 * позицию» (label/type/brand) либо через КП import (1:1 copy от КП
 * compositionRows).
 *
 * Каждый new block (через ADD_SPEC_BLOCK reducer action) auto-seeds эту
 * single row. Custom rows добавляются через ADD_CUSTOM_SPEC_ROW separately
 * (key='custom-<uuid>', removable: true, type/brand/cost optional).
 */
export function buildDefaultSpecRows(): ContractSpecRow[] {
  return [
    {
      id: genUuid(),
      key: 'block_cost',
      label: 'Стоимость',
      cost: 0,
      removable: false,
    },
  ];
}

/**
 * Phase 6.5G Amendment A8 (2026-05-25) — manual seed для пользовательского
 * сценария «Заполнить вручную» (entry с Главной либо «+ Добавить мебель»
 * внутри Договора).
 *
 * Возвращает 17 default позиций (label из Block 12 `DEFAULT_COMPOSITION_ROWS`
 * — Столешница, Фасады, Фурнитура и т.д.) + 1 fixed `block_cost` row.
 * Каждая default row — custom (removable: true) с пустыми type/brand/cost,
 * пользователь дописывает или удаляет лишние.
 *
 * Re-uses Block 12 source of truth (P-001 DRY): label list совпадает с КП
 * «Состав», что упрощает миграцию между Блоком 12 и Блоком 13 (узнаваемый
 * набор для мебельщика).
 */
export function buildManualSpecRows(): ContractSpecRow[] {
  const defaults: ContractSpecRow[] = DEFAULT_COMPOSITION_ROWS.map((source) => {
    const rowId = genUuid();
    return {
      id: rowId,
      key: `custom-${rowId}`,
      label: source.name,
      type: undefined,
      brand: undefined,
      cost: undefined,
      removable: true,
    };
  });
  defaults.push({
    id: genUuid(),
    key: 'block_cost',
    label: 'Стоимость',
    cost: 0,
    removable: false,
  });
  return defaults;
}

/**
 * Phase 6.5B Amendment A1 — currency code derived from region при initial
 * draft creation. User может override через chip-segment в SectionCostAndTerms
 * (UI ships в 6.5C). Этот mapping — source of truth для default только;
 * после first user override `draft.currencyCode` отвязан от region.
 */
export function getDefaultCurrencyCode(region: UserRegion): ContractCurrencyCode {
  return CONTRACT_CURRENCY_PER_REGION[userRegionToContractRegion(region)];
}

/**
 * Phase 6.5F Amendment A7 (2026-05-25) — apply optional seed payload onto
 * default spec rows.
 *
 * **Before (6.5C):** seeded 3 material slots (facade_material/corpus_material/
 * hardware.value) + block_cost.cost + custom rows с combined value.
 *
 * **After (6.5F A7):** seeds только block_cost.cost (single fixed row) +
 * appends custom rows с new shape (type/brand separate fields). Material
 * slots DROPPED per A7.2 (heuristic distribution was source of 6.5D bug + конципуально
 * mismatch с КП composition model).
 *
 * Used by:
 *   1. Reducer `case 'ADD_SPEC_BLOCK'` — atomic «Добавить из КП» dispatch
 *   2. `mapQuoteStateToContractDraft` — initial fromQuote seeding
 */
export function applySpecBlockSeed(
  rows: ContractSpecRow[],
  seedValues?: SpecBlockSeedValues,
  customRows?: SpecBlockSeedCustomRow[],
): ContractSpecRow[] {
  let result = rows;
  if (seedValues && seedValues.block_cost !== undefined) {
    const blockCost = seedValues.block_cost;
    result = result.map((row) => (row.key === 'block_cost' ? { ...row, cost: blockCost } : row));
  }
  if (customRows && customRows.length > 0) {
    const customs: ContractSpecRow[] = customRows.map((cr) => ({
      id: cr.id,
      key: `custom-${cr.id}`,
      label: cr.label,
      type: cr.type,
      brand: cr.brand,
      cost: cr.cost,
      removable: true,
    }));
    result = [...result, ...customs];
  }
  return result;
}

/** Today's ISO date (YYYY-MM-DD) — без time portion для contractDate. */
function todayIsoDate(now: Date = new Date()): string {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Phase 6.5G Amendment A8 (2026-05-25) — entry mode discriminator.
 *
 *   - `'manual'` — initial spec[0].rows = `buildManualSpecRows()` (17 default
 *     позиций + block_cost). Используется когда entry с Главной →
 *     ChoiceSheetCreateMode → «Заполнить вручную» (URL `?manual=true`) либо
 *     прямой URL `/contracts/new` без параметров (per A8.4 default fallback).
 *   - `'from-quote'` — initial spec[0].rows = `buildDefaultSpecRows()` (single
 *     block_cost). `useFromQuoteSeeding` hook HYDRATE-нет actual spec из КП
 *     поверх blank через `mapQuoteStateToContractDraft`. Используется когда
 *     URL `?fromQuote=<uuid>`.
 *   - `'auto'` — alias `'from-quote'` (single block_cost). Backward-compatible
 *     default для callers которые не передают `entryMode` (HYDRATE guard для
 *     старых draft'ов, edge cases).
 */
export type ContractEntryMode = 'manual' | 'from-quote' | 'auto';

export interface CreateInitialContractDraftOpts {
  /** UserRegion из `useAuthStore` (или fallback 'KZ' если auth не загружен). */
  region: UserRegion;
  /** Pre-generated UUID (если контейнер уже сгенерировал) — иначе auto. */
  id?: string;
  /** Default city из template.meta — Container прокидывает после load. Иначе пусто. */
  defaultCity?: string;
  /** Override now для детерминированных тестов. */
  now?: Date;
  /**
   * Phase 6.5G A8 — entry-mode discriminator. Default `'auto'` сохраняет
   * pre-6.5G поведение (single block_cost). См. `ContractEntryMode`.
   */
  entryMode?: ContractEntryMode;
}

/**
 * Создаёт blank `ContractDraft` с дефолтами под регион пользователя.
 *
 * **Не подтягивает fromQuote seed** — это делает `useFromQuoteSeeding` отдельно
 * + `HYDRATE` action в reducer'е (Container merges seed поверх blank).
 */
export function createInitialContractDraft(opts: CreateInitialContractDraftOpts): ContractDraft {
  const { region, id = genUuid(), defaultCity = '', now = new Date(), entryMode = 'auto' } = opts;
  const contractRegion = userRegionToContractRegion(region);
  const defaultLang = CONTRACT_DEFAULT_LANGUAGE_PER_REGION[contractRegion];
  const templateKey: ContractTemplateKey = buildContractTemplateKey(contractRegion, defaultLang);
  const ts = now.getTime();

  const customerRequisites: ContractRequisite[] = buildDefaultRequisites(
    contractRegion,
    'customer',
  );
  const executorRequisites: ContractRequisite[] = buildDefaultRequisites(
    contractRegion,
    'executor',
  );

  // Phase 6.5G A8 — entry-mode driven initial spec rows. `'manual'` → 17
  // default позиций (Block 12 reuse) + block_cost. `'from-quote'` / `'auto'`
  // → single block_cost (HYDRATE поверх blank через useFromQuoteSeeding).
  const initialSpecRows: ContractSpecRow[] =
    entryMode === 'manual' ? buildManualSpecRows() : buildDefaultSpecRows();

  return {
    id,
    templateKey,
    status: 'draft',
    contractNum: '',
    contractDate: todayIsoDate(now),
    contractCity: defaultCity,
    customerName: '',
    customerRepName: '',
    customerRepBasis: '',
    executorName: '',
    executorRepName: '',
    executorRepBasis: '',
    costSource: 'manual',
    // Phase 6.5B Amendment A1 (2026-05-24) — user-controlled currency,
    // default by region.
    currencyCode: getDefaultCurrencyCode(region),
    totalCost: 0,
    advance: 0,
    // PR-D2 (ADR-0040) — стартуем в ручном режиме (ставка не задана).
    advancePercent: null,
    durationDays: 30,
    warrantyTerm: 24,
    warrantyUnit: 'months',
    // Phase 6.5B Amendment A3 — пустой массив custom rows, user добавляет
    // через UI («+ Добавить строку» в SectionCostAndTerms, ships в 6.5C).
    costCustomRows: [],
    // Phase 6.5G A8 — initial spec block с rows per entryMode (см. выше).
    spec: [{ id: genUuid(), title: '', rows: initialSpecRows }],
    customerRequisites,
    executorRequisites,
    comments: '',
    // Phase 7 hotfix round 4 (ADR-0025 A9, 2026-05-26) — default OFF для
    // compliance (auto-clear customer values после PDF). User opts in через
    // checkbox в SectionRequisites для repeat-customer workflow.
    preserveCustomerRequisites: false,
    fromQuoteId: null,
    createdAt: ts,
    updatedAt: ts,
  };
}
